import React, { useState, useRef } from 'react';
import { updatePreferences } from '../services/dashboard';
import './Onboarding.css';

/* ─── SVG Icons ─── */
const GmailIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48">
    <path d="M5.5 8.5h37v31h-37z" fill="#F1F1F1"/>
    <path d="M24 26L5.5 12V8.5L24 22l18.5-13.5V12z" fill="#EA4335"/>
    <path d="M5.5 39.5V12l18.5 14z" fill="#FBBC04"/>
    <path d="M24 26l18.5-14v27.5z" fill="#34A853"/>
    <path d="M42.5 39.5h-37L24 26z" fill="#C5221F" opacity="0.3"/>
  </svg>
);

const OutlookIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48">
    <rect x="6" y="10" width="36" height="28" rx="3" fill="#0078D4"/>
    <path d="M6 14l18 12 18-12" stroke="#fff" strokeWidth="2" fill="none"/>
    <rect x="10" y="18" width="12" height="14" rx="2" fill="#fff" opacity="0.9"/>
    <ellipse cx="16" cy="25" rx="4" ry="5" fill="#0078D4"/>
  </svg>
);

const DocumentIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <rect x="10" y="4" width="28" height="40" rx="4" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
    <path d="M18 16h12M18 22h12M18 28h8" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M28 4v10h10" stroke="#9CA3AF" strokeWidth="2" fill="none" strokeLinejoin="round"/>
  </svg>
);

const CheckCircleIcon = ({ size = 20, color = '#059669' }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill={color}>
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="#F59E0B">
    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
  </svg>
);

const SpinnerIcon = ({ size = 20, color = '#fff' }) => (
  <svg className="onboarding-spinner" width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" opacity="0.25"/>
    <path d="M12 2a10 10 0 019.95 9" stroke={color} strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

/* ─── Step Labels ─── */
const STEP_LABELS = ['Email', 'Resume', 'Preferences', 'Launch'];

const ROLE_OPTIONS = [
  'Software Engineer', 'Frontend Engineer', 'Backend Engineer', 'Full Stack',
  'Data Scientist', 'ML Engineer', 'Data Analyst',
  'Product Manager', 'Product Designer', 'UX Researcher',
  'DevOps / SRE', 'Mobile (iOS/Android)', 'Security Engineer',
  'Marketing', 'Business / Finance', 'Operations',
];

const COMPANY_SIZE_OPTIONS = [
  { label: 'Startup (1-50)', value: 'startup' },
  { label: 'Mid-size (50-500)', value: 'midsize' },
  { label: 'Large (500-5K)', value: 'large' },
  { label: 'Enterprise (5K+)', value: 'enterprise' },
  { label: 'No preference', value: 'any' },
];

const FRESHNESS_OPTIONS = [
  { label: 'Last 24 hours', value: '24h', desc: 'Highest response rates' },
  { label: 'Last 48 hours', value: '48h', desc: 'Recommended' },
  { label: 'Last 7 days', value: '7d', desc: 'Widest net' },
];

const VOLUME_OPTIONS = [
  { label: 'Low', value: 'low', desc: 'A few handpicked matches per day' },
  { label: 'Medium', value: 'medium', desc: 'Balanced quality and volume' },
  { label: 'High', value: 'high', desc: 'Maximize applications within your plan' },
];

/* ─── Progress Bar ─── */
function ProgressBar({ currentStep }) {
  return (
    <div className="onboarding-progress">
      <p className="onboarding-progress-label">Step {currentStep + 1} of 4</p>
      <div className="onboarding-progress-track">
        {STEP_LABELS.map((label, i) => {
          const isCompleted = i < currentStep;
          const isCurrent = i === currentStep;
          const isFuture = i > currentStep;
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <div className={`onboarding-progress-line ${isCompleted ? 'completed' : isCurrent ? 'current' : ''}`} />
              )}
              <div className="onboarding-progress-step">
                <div className={`onboarding-progress-circle ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isFuture ? 'future' : ''}`}>
                  {i + 1}
                </div>
                <span className={`onboarding-progress-step-label ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                  {label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Step 1: Connect Email ─── */
function StepEmail({ emailProvider, setEmailProvider, emailConnecting, setEmailConnecting, emailConnected, setEmailConnected, connectedEmail, setConnectedEmail }) {

  const handleConnect = (provider) => {
    setEmailProvider(provider);
    setEmailConnecting(true);
    setTimeout(() => {
      setEmailConnecting(false);
      setEmailConnected(true);
      setConnectedEmail(provider === 'gmail' ? 'alex@gmail.com' : 'alex@outlook.com');
    }, 2000);
  };

  const permissionRows = [
    { text: 'Send emails from your address', allowed: true },
    { text: 'Read your inbox', allowed: false },
    { text: 'Access your contacts', allowed: false },
    { text: 'Access your Drive', allowed: false },
  ];

  if (emailConnected) {
    return (
      <div className="onboarding-step-content">
        <h2>Connect your email</h2>
        <p className="onboarding-subtitle">We only request send permissions. We cannot read your inbox, personal emails, or attachments.</p>
        <div className="onboarding-email-success">
          <div className="onboarding-success-check">
            <svg className="onboarding-checkmark-svg" viewBox="0 0 52 52">
              <circle className="onboarding-checkmark-circle" cx="26" cy="26" r="24" fill="none"/>
              <path className="onboarding-checkmark-path" fill="none" d="M14 27l8 8 16-16"/>
            </svg>
          </div>
          <p className="onboarding-success-title">{emailProvider === 'gmail' ? 'Gmail' : 'Outlook'} connected successfully</p>
          <p className="onboarding-success-email">{connectedEmail}</p>
        </div>
      </div>
    );
  }

  if (emailConnecting) {
    return (
      <div className="onboarding-step-content">
        <h2>Connect your email</h2>
        <p className="onboarding-subtitle">We only request send permissions. We cannot read your inbox, personal emails, or attachments.</p>
        <div className="onboarding-email-connecting">
          <SpinnerIcon size={40} color="#2563EB" />
          <p>Connecting to {emailProvider === 'gmail' ? 'Gmail' : 'Outlook'}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-step-content">
      <h2>Connect your email</h2>
      <p className="onboarding-subtitle">We only request send permissions. We cannot read your inbox, personal emails, or attachments.</p>
      <div className="onboarding-email-cards">
        <button className="onboarding-email-card" onClick={() => handleConnect('gmail')}>
          <div className="onboarding-email-card-header">
            <GmailIcon />
            <span className="onboarding-email-card-title">Gmail</span>
            <span className="onboarding-email-badge">Recommended</span>
          </div>
          <ul className="onboarding-email-permissions">
            {permissionRows.map((row, i) => (
              <li key={i} className={row.allowed ? 'allowed' : 'denied'}>
                <span className="onboarding-perm-icon">{row.allowed ? '\u2713' : '\u2717'}</span>
                {row.text}
              </li>
            ))}
          </ul>
        </button>
        <button className="onboarding-email-card" onClick={() => handleConnect('outlook')}>
          <div className="onboarding-email-card-header">
            <OutlookIcon />
            <span className="onboarding-email-card-title">Outlook</span>
          </div>
          <ul className="onboarding-email-permissions">
            {permissionRows.map((row, i) => (
              <li key={i} className={row.allowed ? 'allowed' : 'denied'}>
                <span className="onboarding-perm-icon">{row.allowed ? '\u2713' : '\u2717'}</span>
                {row.text}
              </li>
            ))}
          </ul>
        </button>
      </div>
    </div>
  );
}

/* ─── Step 2: Upload Resume ─── */
function StepResume({ resumeFile, setResumeFile, resumeParsing, setResumeParsing, resumeParsed, setResumeParsed, parsedTags, setParsedTags }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    setResumeFile(file);
    setResumeParsing(true);
    setTimeout(() => {
      setResumeParsing(false);
      setResumeParsed(true);
      setParsedTags(['Python', 'JavaScript', 'React', 'Stanford University', '2 internships']);
    }, 2000);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleRemove = () => {
    setResumeFile(null);
    setResumeParsing(false);
    setResumeParsed(false);
    setParsedTags([]);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="onboarding-step-content">
      <h2>Upload your resume</h2>
      <p className="onboarding-subtitle">We use it to match you with relevant roles and personalize your applications.</p>

      {!resumeFile && (
        <div
          className={`onboarding-dropzone ${dragOver ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <DocumentIcon />
          <p className="onboarding-dropzone-text">Drag your resume here or click to browse</p>
          <p className="onboarding-dropzone-hint">PDF, DOCX, or TXT — max 5MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt"
            className="onboarding-file-input"
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>
      )}

      {resumeFile && !resumeParsed && (
        <div className="onboarding-file-card">
          <div className="onboarding-file-info">
            <DocumentIcon />
            <div>
              <p className="onboarding-file-name">{resumeFile.name}</p>
              <p className="onboarding-file-size">{formatSize(resumeFile.size)}</p>
            </div>
          </div>
          {resumeParsing && (
            <div className="onboarding-parsing">
              <p>Parsing your resume...</p>
              <div className="onboarding-parsing-bar">
                <div className="onboarding-parsing-fill" />
              </div>
            </div>
          )}
        </div>
      )}

      {resumeParsed && (
        <>
          <div className="onboarding-file-card">
            <div className="onboarding-file-info">
              <CheckCircleIcon size={24} />
              <div>
                <p className="onboarding-file-name">{resumeFile.name}</p>
                <p className="onboarding-file-size">{formatSize(resumeFile.size)}</p>
              </div>
            </div>
            <button className="onboarding-remove-btn" onClick={handleRemove}>&times; Remove</button>
          </div>
          <div className="onboarding-parsed-tags">
            {parsedTags.map((tag, i) => (
              <span key={i} className="onboarding-tag">{tag}</span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Multi-Select Dropdown ─── */
function MultiSelectDropdown({ options, selected, onToggle, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="onboarding-multiselect" ref={ref}>
      <button className="onboarding-multiselect-trigger" onClick={() => setOpen(!open)}>
        {selected.length === 0 ? <span className="onboarding-multiselect-placeholder">{placeholder}</span> : (
          <span className="onboarding-multiselect-tags">
            {selected.slice(0, 3).map(s => <span key={s} className="onboarding-multiselect-tag">{s}</span>)}
            {selected.length > 3 && <span className="onboarding-multiselect-more">+{selected.length - 3}</span>}
          </span>
        )}
        <span className="onboarding-multiselect-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="onboarding-multiselect-dropdown">
          {options.map(opt => (
            <label key={opt} className={`onboarding-multiselect-option ${selected.includes(opt) ? 'selected' : ''}`}>
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => onToggle(opt)} />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Step 3: Preferences ─── */
function StepPreferences({
  selectedRoles, setSelectedRoles,
  remoteEnabled, setRemoteEnabled,
  locations, setLocations,
  minPay, setMinPay,
  companySize, setCompanySize,
  freshness, setFreshness,
  coldEmailEnabled, setColdEmailEnabled,
  dailyVolume, setDailyVolume,
}) {
  const [locationInput, setLocationInput] = useState('');

  const toggleRole = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const addLocation = () => {
    const trimmed = locationInput.trim();
    if (trimmed && !locations.includes(trimmed)) {
      setLocations(prev => [...prev, trimmed]);
      setLocationInput('');
    }
  };

  const removeLocation = (loc) => {
    setLocations(prev => prev.filter(l => l !== loc));
  };

  return (
    <div className="onboarding-step-content">
      <h2>Set your preferences</h2>
      <p className="onboarding-subtitle">We'll only apply to roles that match. Change these anytime from your dashboard.</p>

      {/* Role types — multi-select dropdown */}
      <div className="onboarding-pref-section">
        <label className="onboarding-pref-label">What roles interest you?</label>
        <MultiSelectDropdown
          options={ROLE_OPTIONS}
          selected={selectedRoles}
          onToggle={toggleRole}
          placeholder="Select roles..."
        />
      </div>

      {/* Location */}
      <div className="onboarding-pref-section">
        <label className="onboarding-pref-label">Where do you want to work?</label>
        <div className="onboarding-remote-row">
          <span>Include remote positions</span>
          <button
            className={`onboarding-toggle ${remoteEnabled ? 'on' : ''}`}
            onClick={() => setRemoteEnabled(!remoteEnabled)}
            aria-label="Toggle remote"
          >
            <span className="onboarding-toggle-knob" />
          </button>
        </div>
        <div className="onboarding-location-input-row">
          <input
            type="text"
            placeholder="e.g. San Francisco, New York, Austin..."
            value={locationInput}
            onChange={(e) => setLocationInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addLocation()}
            className="onboarding-text-input"
          />
          <button className="onboarding-add-btn" onClick={addLocation}>Add</button>
        </div>
        {locations.length > 0 && (
          <div className="onboarding-location-tags">
            {locations.map((loc, i) => (
              <span key={i} className="onboarding-location-tag">
                {loc}
                <button onClick={() => removeLocation(loc)}>&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Min pay + Company size — side by side */}
      <div className="onboarding-pref-row">
        <div className="onboarding-pref-section" style={{ flex: 1 }}>
          <label className="onboarding-pref-label">Minimum hourly rate</label>
          <div className="onboarding-pay-input-row">
            <span className="onboarding-pay-prefix">$</span>
            <input
              type="number"
              min="0"
              placeholder="0"
              value={minPay}
              onChange={(e) => setMinPay(e.target.value)}
              className="onboarding-text-input onboarding-pay-input"
            />
            <span className="onboarding-pay-suffix">/ hr</span>
          </div>
        </div>
        <div className="onboarding-pref-section" style={{ flex: 1 }}>
          <label className="onboarding-pref-label">Company size</label>
          <MultiSelectDropdown
            options={COMPANY_SIZE_OPTIONS.map(o => o.label)}
            selected={Array.isArray(companySize) ? companySize : []}
            onToggle={(label) => {
              setCompanySize(prev => {
                const arr = Array.isArray(prev) ? prev : [];
                return arr.includes(label) ? arr.filter(l => l !== label) : [...arr, label];
              });
            }}
            placeholder="No preference"
          />
        </div>
      </div>

      {/* Listing freshness */}
      <div className="onboarding-pref-section">
        <label className="onboarding-pref-label">How fresh should job postings be?</label>
        <div className="onboarding-segmented">
          {FRESHNESS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`onboarding-seg-btn ${freshness === opt.value ? 'active' : ''}`}
              onClick={() => setFreshness(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cold emails */}
      <div className="onboarding-pref-section">
        <div className="onboarding-cold-email-row">
          <div>
            <label className="onboarding-pref-label">Enable cold email outreach</label>
            <p className="onboarding-pref-desc">Personalized emails to recruiters. You approve every email before it sends.</p>
          </div>
          <button
            className={`onboarding-toggle ${coldEmailEnabled ? 'on' : ''}`}
            onClick={() => setColdEmailEnabled(!coldEmailEnabled)}
            aria-label="Toggle cold emails"
          >
            <span className="onboarding-toggle-knob" />
          </button>
        </div>
      </div>

      {/* Daily volume — based on plan */}
      <div className="onboarding-pref-section">
        <label className="onboarding-pref-label">How many applications per day?</label>
        <div className="onboarding-segmented">
          {(VOLUME_OPTIONS).map(opt => (
            <button
              key={opt.value}
              className={`onboarding-seg-btn ${dailyVolume === opt.value ? 'active' : ''}`}
              onClick={() => setDailyVolume(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {dailyVolume && (
          <p className="onboarding-pref-hint">
            {VOLUME_OPTIONS.find(o => o.value === dailyVolume)?.desc}
          </p>
        )}
      </div>
    </div>
  );
}

/* ─── Step 4: Review & Launch ─── */
function StepLaunch({
  emailConnected, connectedEmail, emailProvider,
  resumeFile, resumeParsed,
  selectedRoles, remoteEnabled, locations,
  minPay, coldEmailEnabled, dailyVolume,
  companySize, freshness,
  launching, setLaunching, launched, setLaunched,
  toasts, setToasts,
  goToStep, onComplete,
}) {

  const savePreferences = async () => {
    try {
      await updatePreferences({
        roles: selectedRoles,
        locations: locations,
        min_pay: minPay ? parseInt(minPay) : 0,
        remote: remoteEnabled,
        cold_email_enabled: coldEmailEnabled,
        daily_volume: dailyVolume,
        company_size: companySize,
        listing_freshness: freshness,
      });
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
  };

  const handleLaunch = () => {
    setLaunching(true);
    savePreferences();
    setTimeout(() => {
      setLaunching(false);
      setLaunched(true);
      // Stagger toasts
      setTimeout(() => setToasts([0]), 400);
      setTimeout(() => setToasts([0, 1]), 800);
      setTimeout(() => setToasts([0, 1, 2]), 1200);
      // Navigate away
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 3700);
    }, 1500);
  };

  const toastMessages = [
    { emoji: '\uD83D\uDD0D', text: 'Scanning 1,247 new listings...' },
    { emoji: '\uD83D\uDCC4', text: 'Found 23 matches for your profile' },
    { emoji: '\uD83D\uDE80', text: 'First application sending...' },
  ];

  const locationStr = [
    ...(remoteEnabled ? ['Remote'] : []),
    ...locations,
  ].join(', ') || 'Not set';

  const rolesStr = selectedRoles.length > 0 ? selectedRoles.join(', ') : 'None selected';

  const summaryRows = [
    {
      label: 'Email',
      value: emailConnected ? connectedEmail : 'Not connected',
      status: emailConnected ? 'ok' : 'warn',
      editStep: 0,
    },
    {
      label: 'Resume',
      value: resumeParsed && resumeFile ? resumeFile.name : 'Not uploaded',
      status: resumeParsed ? 'ok' : 'warn',
      editStep: 1,
    },
    { label: 'Roles', value: rolesStr, editStep: 2 },
    { label: 'Locations', value: locationStr, editStep: 2 },
    { label: 'Min pay', value: minPay, editStep: 2 },
    { label: 'Cold emails', value: coldEmailEnabled ? 'Enabled' : 'Disabled', editStep: 2 },
    { label: 'Daily volume', value: dailyVolume, editStep: 2 },
  ];

  return (
    <div className="onboarding-step-content">
      <h2>Ready to launch?</h2>
      <p className="onboarding-subtitle">Here's what ApplyAI will do for you.</p>

      <div className="onboarding-summary">
        {summaryRows.map((row, i) => (
          <div key={i} className="onboarding-summary-row">
            <div className="onboarding-summary-left">
              {row.status === 'ok' && <CheckCircleIcon size={18} />}
              {row.status === 'warn' && <WarningIcon />}
              <span className="onboarding-summary-label">{row.label}:</span>
              <span className="onboarding-summary-value">{row.value}</span>
            </div>
            <button className="onboarding-edit-link" onClick={() => goToStep(row.editStep)}>Edit</button>
          </div>
        ))}
      </div>

      <button
        className={`onboarding-launch-btn ${launched ? 'launched' : ''}`}
        onClick={handleLaunch}
        disabled={launching || launched}
      >
        {launching && <><SpinnerIcon size={20} /> Starting...</>}
        {launched && <>You're live! \u2713</>}
        {!launching && !launched && <>Start Applying &rarr;</>}
      </button>

      {/* Toasts */}
      <div className="onboarding-toasts">
        {toastMessages.map((t, i) => (
          <div
            key={i}
            className={`onboarding-toast ${toasts.includes(i) ? 'visible' : ''}`}
          >
            <span className="onboarding-toast-emoji">{t.emoji}</span>
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Onboarding Component ─── */
export default function Onboarding({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);

  // Step 1 state
  const [emailProvider, setEmailProvider] = useState(null);
  const [emailConnecting, setEmailConnecting] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');

  // Step 2 state
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeParsing, setResumeParsing] = useState(false);
  const [resumeParsed, setResumeParsed] = useState(false);
  const [parsedTags, setParsedTags] = useState([]);

  // Step 3 state
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [remoteEnabled, setRemoteEnabled] = useState(true);
  const [locations, setLocations] = useState([]);
  const [minPay, setMinPay] = useState('');
  const [companySize, setCompanySize] = useState([]);
  const [freshness, setFreshness] = useState('48h');
  const [coldEmailEnabled, setColdEmailEnabled] = useState(true);
  const [dailyVolume, setDailyVolume] = useState('medium');

  // Step 4 state
  const [launching, setLaunching] = useState(false);
  const [launched, setLaunched] = useState(false);
  const [toasts, setToasts] = useState([]);

  const goToStep = (step) => setCurrentStep(step);
  const handleBack = () => setCurrentStep(prev => Math.max(0, prev - 1));
  const handleContinue = () => setCurrentStep(prev => Math.min(3, prev + 1));

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <ProgressBar currentStep={currentStep} />

        <div className="onboarding-card">
          {currentStep === 0 && (
            <StepEmail
              emailProvider={emailProvider} setEmailProvider={setEmailProvider}
              emailConnecting={emailConnecting} setEmailConnecting={setEmailConnecting}
              emailConnected={emailConnected} setEmailConnected={setEmailConnected}
              connectedEmail={connectedEmail} setConnectedEmail={setConnectedEmail}
            />
          )}

          {currentStep === 1 && (
            <StepResume
              resumeFile={resumeFile} setResumeFile={setResumeFile}
              resumeParsing={resumeParsing} setResumeParsing={setResumeParsing}
              resumeParsed={resumeParsed} setResumeParsed={setResumeParsed}
              parsedTags={parsedTags} setParsedTags={setParsedTags}
            />
          )}

          {currentStep === 2 && (
            <StepPreferences
              selectedRoles={selectedRoles} setSelectedRoles={setSelectedRoles}
              remoteEnabled={remoteEnabled} setRemoteEnabled={setRemoteEnabled}
              locations={locations} setLocations={setLocations}
              minPay={minPay} setMinPay={setMinPay}
              companySize={companySize} setCompanySize={setCompanySize}
              freshness={freshness} setFreshness={setFreshness}
              coldEmailEnabled={coldEmailEnabled} setColdEmailEnabled={setColdEmailEnabled}
              dailyVolume={dailyVolume} setDailyVolume={setDailyVolume}
            />
          )}

          {currentStep === 3 && (
            <StepLaunch
              emailConnected={emailConnected} connectedEmail={connectedEmail} emailProvider={emailProvider}
              resumeFile={resumeFile} resumeParsed={resumeParsed}
              selectedRoles={selectedRoles} remoteEnabled={remoteEnabled} locations={locations}
              minPay={minPay} coldEmailEnabled={coldEmailEnabled} dailyVolume={dailyVolume}
              companySize={companySize} freshness={freshness}
              launching={launching} setLaunching={setLaunching}
              launched={launched} setLaunched={setLaunched}
              toasts={toasts} setToasts={setToasts}
              goToStep={goToStep} onComplete={onComplete}
            />
          )}

          {/* Navigation */}
          {currentStep < 3 && (
            <div className="onboarding-nav">
              <div className="onboarding-nav-left">
                {currentStep > 0 && (
                  <button className="onboarding-back-btn" onClick={handleBack}>
                    &larr; Back
                  </button>
                )}
              </div>
              <div className="onboarding-nav-right">
                {(currentStep === 0 && !emailConnected) || (currentStep === 1 && !resumeParsed) ? (
                  <button className="onboarding-skip-btn" onClick={handleContinue}>
                    Skip for now &rarr;
                  </button>
                ) : null}
                {((currentStep === 0 && emailConnected) || (currentStep === 1 && resumeParsed) || currentStep === 2) && (
                  <button className="onboarding-continue-btn" onClick={handleContinue}>
                    Continue &rarr;
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
